import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { usePagination } from "react-use-pagination";
import { toVWDesktop, toVWMobile } from "../../helpers/styles";
import { BREAKPOINTS } from "../../helpers/theme";
import { BackButton } from "../shared/BackButton";
import { ROUTES } from "../../helpers/routes";
import Header from "./shared/Header";
import SearchTags from "../SearchTags";
import { Tag } from "../../types";
import { Paginator } from "../shared/Paginator";
import PressReleaseCard, { PressReleaseCardProps } from "../PressReleaseCard";
import { searchInObject } from "../../helpers/search";
import { getQueryParams } from "../../helpers/location";

export type PressReleaseSearchProps = {
  title: string;
  backLabel: string;
  searchLabel: string;
  pressReleases: Array<PressReleaseCardProps>;
  tags: Array<Tag>;
};

const PressReleaseSearch: FC<PressReleaseSearchProps> = ({
  title,
  backLabel,
  searchLabel,
  pressReleases = [],
  tags,
}: PressReleaseSearchProps) => {
  const [activeTag, setActiveTag] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const filteredCardList = useMemo(() => {
    return pressReleases.filter(
      (pRelease) =>
        (searchTerm ? searchInObject(pRelease, searchTerm) : true) &&
        pRelease?.tags?.find(
          (tag) =>
            tag.value === tags[activeTag]?.value || !tags[activeTag]?.value
        )
    );
  }, [pressReleases, searchTerm, activeTag]);

  const {
    currentPage,
    totalPages,
    setPage,
    setNextPage,
    setPreviousPage,
    nextEnabled,
    previousEnabled,
    startIndex,
    endIndex,
  } = usePagination({
    totalItems: filteredCardList?.length ?? 0,
    initialPageSize: 9,
  });

  useEffect(() => {
    const queryParams = getQueryParams();
    const currentTagIndex = tags?.findIndex(
      (tag) => tag?.value?.toLowerCase() === queryParams?.tag?.toLowerCase()
    );
    currentTagIndex >= 0 && setActiveTag(currentTagIndex);
    setSearchTerm(queryParams?.query);
  }, []);

  const handleActiveTagChange = useCallback(
    (activeTagIndex: number) => {
      setActiveTag(activeTagIndex);
    },
    [setActiveTag]
  );

  const handlePageChange = useCallback(
    (active: number) => {
      setPage(active);
    },
    [setPage]
  );

  const handleNextPage = useCallback(() => {
    setNextPage();
  }, [setNextPage]);

  const handlePrevPage = useCallback(() => {
    setPreviousPage();
  }, [setPreviousPage]);

  useEffect(() => {
    window?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentPage]);

  return (
    <Container>
      <BackButton label={backLabel} link={`${ROUTES.NEWSROOM}#newsroom`} />
      <Header title={title} />

      <StyledSearchTags
        tags={tags}
        searchPlaceholder={searchLabel}
        setActive={handleActiveTagChange}
        active={activeTag}
        searchTerm={searchTerm}
        onSearchTermChange={(v) => setSearchTerm(v)}
      />
      <CardsContainer id={"cards-container"}>
        {filteredCardList
          .slice(startIndex, endIndex + 1)
          .map((rCard, index) => (
            <PressReleaseCard key={index} {...rCard} />
          ))}
      </CardsContainer>

      <Paginator
        totalPages={totalPages}
        currentPage={currentPage + 1}
        onClick={handlePageChange}
        onNext={handleNextPage}
        onNextDisabled={!nextEnabled}
        onPrev={handlePrevPage}
        onPrevDisabled={!previousEnabled}
      />
    </Container>
  );
};

const Container = styled.div`
  background-color: var(--color-green-90);
  display: flex;
  flex-direction: column;
  padding: ${toVWMobile(16)}vw ${toVWMobile(19)}vw ${toVWMobile(80)}vw;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding: ${toVWDesktop(42)}vw ${toVWDesktop(100)}vw ${toVWDesktop(80)}vw;
  }
`;

const StyledSearchTags = styled(SearchTags)`
  margin-top: ${toVWMobile(40)}vw;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin-top: ${toVWDesktop(60)}vw;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  gap: ${toVWMobile(40)}vw;
  margin-top: ${toVWMobile(12)}vw;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin-top: 0;
    gap: initial;
    display: grid; /* 1 */
    grid-template-columns: repeat(auto-fill, ${toVWDesktop(400)}vw); /* 2 */
    grid-gap: ${toVWDesktop(80)}vw ${toVWDesktop(10)}vw;
    justify-content: space-between; /* 4 */
    flex-direction: initial;
  }
`;

export default PressReleaseSearch;
